// Picking one of 118 characters from a plain <select> means an unstyled
// browser list the length of the screen. This is a button that opens a panel
// with a search box, the character's face beside every name, and the list
// filtered as you type. Keyboard: type to filter, arrows to move, Enter to
// choose, Escape to close.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import './CharacterPicker.css';

const CharacterPicker = ({ characters, value, onChange, label }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const selected = characters.find((c) => c.slug === value);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return characters;
    return characters.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.crew || '').toLowerCase().includes(q) ||
        (c.aliases || []).some((alias) => alias.toLowerCase().includes(q)),
    );
  }, [characters, query]);

  // Close when the click lands anywhere else, and when the page scrolls away.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
    if (!open) {
      setQuery('');
      setActive(0);
    }
  }, [open]);

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const row = listRef.current.children[active];
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const choose = (slug) => {
    onChange(slug);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && matches[active]) {
      e.preventDefault();
      choose(matches[active].slug);
    }
  };

  return (
    <div className="cp-root" ref={rootRef}>
      <button
        type="button"
        className={`cp-button ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
      >
        {selected && <img src={selected.image} alt="" className="cp-button-face" loading="lazy" />}
        <span className="cp-button-name">{selected ? selected.name : 'Pick a character'}</span>
        <span className="cp-caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="cp-panel" role="listbox" aria-label={label}>
          <input
            ref={searchRef}
            type="text"
            className="cp-search"
            placeholder="Search a name or a crew"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
          />

          <div className="cp-list" ref={listRef}>
            {matches.map((c, i) => (
              <button
                type="button"
                key={c.slug}
                role="option"
                aria-selected={c.slug === value}
                className={`cp-option ${c.slug === value ? 'is-selected' : ''} ${i === active ? 'is-active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(c.slug)}
              >
                <img src={c.image} alt="" className="cp-face" loading="lazy" />
                <span className="cp-name">{c.name}</span>
                {c.crew && <span className="cp-crew">{c.crew}</span>}
              </button>
            ))}

            {matches.length === 0 && <p className="cp-empty">No character by that name.</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default CharacterPicker;
